import { useEffect, useRef } from "react";

import { blockedFor, markShown } from "./popupStorage";

// Fast movement is coalesced, so the last event before the pointer leaves can
// sit tens of pixels below the edge — hence a band rather than an exact 0.
const TOP_BAND = 50;
const DROPDOWN_GUARD_MS = 1000;
const MOVE_THROTTLE_MS = 250;
const VIDEO_RECHECK_MS = 2000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "pointerdown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
];

// A native <select> hands its popup to the OS, and the browser then fires the
// same "pointer left the document" mouseout that a real exit produces.
const NATIVE_DROPDOWN_SELECTOR = [
  "select",
  "option",
  "optgroup",
  "[data-popup-ignore-exit]",
].join(",");

// Only matched against the focused element or a recent click — never against
// the event target's ancestors, or an open accordion (aria-expanded) would
// suppress exit intent for the whole section below it.
const OPEN_DROPDOWN_SELECTOR = [
  NATIVE_DROPDOWN_SELECTOR,
  "[role='listbox']",
  "[role='combobox']",
  "[aria-haspopup='listbox']",
  "[aria-haspopup='menu']",
  "[aria-expanded='true']",
].join(",");

const matches = (node, selector) => {
  if (!node || typeof node.closest !== "function") return false;

  try {
    return Boolean(node.closest(selector));
  } catch (error) {
    return false;
  }
};

const isVideoPlaying = () => {
  const media = Array.from(document.querySelectorAll("video, audio"));
  if (
    media.some((el) => !el.paused && !el.ended && el.readyState > 2)
  ) {
    return true;
  }

  // Embeds are opaque from outside. The landing video modal only exists in the
  // DOM while it is open and autoplaying; others opt in with the attribute.
  return Boolean(
    document.querySelector(
      ".landing_video_modal, [data-popup-video-playing='true']"
    )
  );
};

const isExcludedPath = (pathname, rules) => {
  const list = String(rules || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!list.length) return false;

  const path = pathname.endsWith("/") ? pathname : `${pathname}/`;

  return list.some((rule) => {
    if (rule.endsWith("*")) return path.startsWith(rule.slice(0, -1));
    return path === (rule.endsWith("/") ? rule : `${rule}/`);
  });
};

// Slots already used this page load: "any" when the popups share a limit, the
// trigger names when they run independently. Module scope so it survives
// client-side route changes; only a real reload clears it.
const shownThisPageLoad = {};

// Companion to clearLocks() — the stored locks are only half the picture.
const forgetThisPageLoad = () => {
  Object.keys(shownThisPageLoad).forEach((slot) => {
    delete shownThisPageLoad[slot];
  });
};

const usePopupTriggers = ({ settings, pathname, isOpen, onOpen, onRetry, tick }) => {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const onRetryRef = useRef(onRetry);
  onRetryRef.current = onRetry;

  const hasInteractedRef = useRef(false);
  const routeCountRef = useRef(0);

  useEffect(() => {
    routeCountRef.current += 1;
    // Navigating to another page is itself an interaction.
    if (routeCountRef.current > 1) hasInteractedRef.current = true;
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const general = settings?.general;
    if (!settings) return undefined;
    if (general?.popupsEnabled === false) return undefined;
    if (isExcludedPath(pathname, general?.excludedPaths)) return undefined;
    if (isOpen) return undefined;

    const reopenAfter = Number(general?.reopenAfterMinutes) || 0;
    // Falls back until the options page has been saved at least once.
    const frequency = general?.showFrequency || "session";
    const independent = Boolean(general?.independentPopups);
    const exit = settings.exitIntentPopup;
    const idle = settings.inactivityPopup;

    const slotFor = (trigger) => (independent ? trigger : "any");
    const lockFor = (trigger) =>
      shownThisPageLoad[slotFor(trigger)]
        ? Infinity
        : blockedFor(trigger, frequency, reopenAfter, independent);

    const isTouch = !window.matchMedia("(hover: hover) and (pointer: fine)")
      .matches;

    const allowedHere = (config) =>
      Boolean(config?.enabled) && (!isTouch || Boolean(config?.enableOnMobile));

    const exitBlocked = allowedHere(exit) ? lockFor("exit-intent") : Infinity;
    const idleBlocked = allowedHere(idle) ? lockFor("inactivity") : Infinity;

    const exitEnabled = exitBlocked === null;
    const idleEnabled = idleBlocked === null;

    if (!exitEnabled && !idleEnabled) {
      // A lock that expires on a timer rather than with the session: come back.
      const wait = Math.min(exitBlocked, idleBlocked);
      if (!Number.isFinite(wait)) return undefined;

      const id = setTimeout(() => onRetryRef.current(), wait + 100);
      return () => clearTimeout(id);
    }

    let disposed = false;
    let fired = false;
    let armed = !exitEnabled;
    let lastDropdownAt = 0;
    let lastReset = 0;
    let lastY = null;
    let idleTimer = null;
    let armTimer = null;

    const clearIdleTimer = () => {
      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
    };

    const fire = (trigger) => {
      if (disposed || fired || shownThisPageLoad[slotFor(trigger)]) return;
      fired = true;
      shownThisPageLoad[slotFor(trigger)] = true;
      clearIdleTimer();
      markShown(trigger, frequency, independent);
      onOpenRef.current(trigger);
    };

    const noteDropdown = (event) => {
      if (matches(event.target, OPEN_DROPDOWN_SELECTOR)) {
        lastDropdownAt = Date.now();
      }
    };

    const trackPointer = (event) => {
      lastY = event.clientY;
    };

    // Top band plus upward travel — the direction is what separates this from
    // an exit through the sides or the bottom.
    const leftThroughTop = (event) => {
      const y = Number.isFinite(event.clientY) ? event.clientY : lastY;
      if (y === null || y > TOP_BAND) return false;
      if (lastY === null) return true;

      return y <= lastY;
    };

    const onLeaveWindow = (event) => {
      if (!armed || fired || document.hidden) return;
      // Only empty when the pointer left the document itself.
      if (event.relatedTarget || event.toElement) return;
      if (!leftThroughTop(event)) return;
      if (matches(event.target, NATIVE_DROPDOWN_SELECTOR)) return;
      if (matches(document.activeElement, OPEN_DROPDOWN_SELECTOR)) return;
      if (Date.now() - lastDropdownAt < DROPDOWN_GUARD_MS) return;

      fire("exit-intent");
    };

    if (exitEnabled) {
      const armAfter = Math.max(0, Number(exit?.armAfterSeconds) || 0) * 1000;
      armTimer = setTimeout(() => {
        armed = true;
      }, armAfter);

      document.addEventListener("mousemove", trackPointer, { passive: true });
      // Browsers differ in which of the two they deliver; `fired` dedupes.
      document.addEventListener("mouseout", onLeaveWindow);
      document.documentElement.addEventListener("mouseleave", onLeaveWindow);
      document.addEventListener("mousedown", noteDropdown, true);
      document.addEventListener("keydown", noteDropdown, true);
      document.addEventListener("change", noteDropdown, true);
    }

    const delayMs =
      Math.max(1, Number(idle?.inactivitySeconds) || 10) * 1000;
    const requiresInteraction = idle?.requirePriorInteraction !== false;
    const pausesOnVideo = idle?.pauseWhenVideoPlaying !== false;

    const onIdle = () => {
      idleTimer = null;
      if (disposed || fired || document.hidden) return;

      if (pausesOnVideo && isVideoPlaying()) {
        idleTimer = setTimeout(onIdle, VIDEO_RECHECK_MS);
        return;
      }

      fire("inactivity");
    };

    const scheduleIdle = () => {
      if (!idleEnabled || disposed || fired) return;
      clearIdleTimer();
      if (requiresInteraction && !hasInteractedRef.current) return;
      idleTimer = setTimeout(onIdle, delayMs);
    };

    const onActivity = (event) => {
      const now = Date.now();
      hasInteractedRef.current = true;

      if (event.type === "mousemove" && now - lastReset < MOVE_THROTTLE_MS) {
        return;
      }
      lastReset = now;
      scheduleIdle();
    };

    const onVisibilityChange = () => {
      if (document.hidden) clearIdleTimer();
      else scheduleIdle();
    };

    if (idleEnabled) {
      ACTIVITY_EVENTS.forEach((type) => {
        document.addEventListener(type, onActivity, { passive: true });
      });
      document.addEventListener("visibilitychange", onVisibilityChange);
      scheduleIdle();
    }

    return () => {
      disposed = true;
      clearIdleTimer();
      if (armTimer) clearTimeout(armTimer);

      document.removeEventListener("mousemove", trackPointer, { passive: true });
      document.removeEventListener("mouseout", onLeaveWindow);
      document.documentElement.removeEventListener("mouseleave", onLeaveWindow);
      document.removeEventListener("mousedown", noteDropdown, true);
      document.removeEventListener("keydown", noteDropdown, true);
      document.removeEventListener("change", noteDropdown, true);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      ACTIVITY_EVENTS.forEach((type) => {
        document.removeEventListener(type, onActivity, { passive: true });
      });
    };
  }, [settings, pathname, isOpen, tick]);
};

export default usePopupTriggers;
export { forgetThisPageLoad };
